import React, { useState, useEffect } from 'react'

import clsx from 'clsx'

import Avatars from 'components/Avatars'
import Button from 'components/Button'
import Divider from 'components/Divider'
import Grid from 'components/Grid'
import Icon from 'components/Icon'
import InfoCircleTooltip from 'components/InfoCircleTooltip'
import MultiColorProgressBar from 'components/MultiColorProgressBar'
import Paper from 'components/Paper'
import Review from 'components/Review'
import ReviewMethodologyModal from 'components/ReviewMethodologyModal'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import { thousandSeparator } from 'helpers/formatters'

import RatingRank from './components/RatingRank'
import { PRO } from './constants'

import classes from './ReviewsSummary.module.scss'

const MAX_REVIEWERS = 3

const addAvatarsFromSnippets = (reviewers, snippets) => {
  if (!snippets) return

  for (let i = 0; i < snippets.length; i++) {
    const avatar = snippets[i]?.avatar
    if (avatar && reviewers.length < MAX_REVIEWERS) {
      reviewers.push(avatar)
    }
    if (reviewers.length >= MAX_REVIEWERS) break
  }
}
const getAvatarsFromTopics = topics => {
  const reviewers = []
  for (let i = 0; i < topics.length && reviewers.length < MAX_REVIEWERS; i++) {
    addAvatarsFromSnippets(reviewers, topics[i].snippets)
  }
  // Fill remaining slots with null avatars
  while (reviewers.length < MAX_REVIEWERS) {
    reviewers.push(null)
  }
  return reviewers.reverse()
}

/** @type {import("./types").default} */
const ReviewsSummary = ({ listing }) => {
  const [activeChip, setActiveChip] = useState('')
  const reviewerUrls = getAvatarsFromTopics(listing.reviews_summaries_v2.topics.slice(0, 3))
  const { addEvent } = useEventContext({
    engagementComponent: 'product_reviews',
    engagementDetails: 'chip_reviews-topic',
    name: 'engagement_product_click',
  })
  const reviewSummary = listing.reviews_summaries_v2

  useEffect(() => {
    if (listing.reviews_summaries_v2) {
      setActiveChip(listing.reviews_summaries_v2.topics?.[0]?.name)
    }
  }, [listing])

  const renderChips = reviewSummary.topics.map(topic => {
    return (
      <Button
        key={topic.name}
        onClick={() => setActiveChip(topic.name)}
        className={clsx(classes.chipButton, {
          [classes.activeChip]: activeChip === topic.name,
        })}
        data-testid="product_reviews-show-reviews-topic-summary"
        {...addEvent({
          engagementDestination: 'show_reviews-topic-summary',
        })}
      >
        {topic.type === 'NEUT' ? (
          ''
        ) : (
          <Icon
            size={10}
            className={clsx(classes[topic.type === PRO ? 'iconPros' : 'iconCons'], classes.icon)}
            icon={topic.type === PRO ? 'caret-circle-up' : 'caret-circle-down'}
          />
        )}
        {topic.title}
      </Button>
    )
  })

  const renderChipsContent = reviewSummary.topics
    .filter(topic => topic.name === activeChip)
    .map(topic => {
      return (
        <p key={topic.name}>
          <Typography variant="tight-sm" className={classes.reviewDescription}>
            {topic.summary}
          </Typography>

          <Grid container spacing={1}>
            {topic.snippets.map(snippet => (
              <Grid item xs={12} key={snippet.id} className={classes.snippetContainer}>
                <Review.Snippet
                  size="large"
                  snippet={snippet}
                  className={classes.snippet}
                  engagementComponent="product_key-features"
                />
              </Grid>
            ))}
          </Grid>
        </p>
      )
    })

  return (
    <Paper className={classes.root}>
      <Grid spacing={3} container>
        <Grid item xs={12} lg={4} className={classes.leftContainer}>
          <Grid spacing={3} container>
            <Grid item xs={12} lg={12} md={6} sm={6}>
              <div className={classes.contributors}>
                {reviewerUrls.filter(item => item !== null).length !== 0 && (
                  <Avatars imageSize={48} imagesUrl={reviewerUrls} className={classes.avatars} />
                )}
                <Typography variant="tight-xxs" className={classes.reviewsTitle}>
                  To determine these pros and cons insights, we analyzed responses from&nbsp;
                  <ReviewMethodologyModal
                    targetComponent="product_customer-support"
                    linkText={`${thousandSeparator(listing.num_reviews)} verified reviews`}
                    className={classes.reviewLink}
                  />
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} lg={12} md={6} sm={6}>
              <div className={classes.ratingSection}>
                <Typography variant="heading-md" className={classes.title}>
                  Overall rating
                </Typography>
                <RatingRank
                  average={listing.reviews.rating_avg}
                  totals={listing.reviews.total}
                  ratings={listing.reviews?.by_field}
                />
              </div>

              <div className={classes.ratingSection}>
                <InfoCircleTooltip
                  className={classes.titleAndIcon}
                  enterTouchDelay={100}
                  leaveTouchDelay={3000}
                  label={<Typography variant="title-8">Reviews sentiment</Typography>}
                  title={`The chart represents the distribution of positive, neutral and negative reviews.`}
                />
                <MultiColorProgressBar reviews={listing.reviews} href={listing.cta_urls.reviews} />
              </div>
            </Grid>
          </Grid>
        </Grid>

        <Grid item md={12} xs={12} lg={8} className={classes.rightContainer}>
          <div className={clsx(classes.divider, classes.dividerLeft)} />
          <div className={classes.titleSection}>
            <Typography component="h3" variant="heading-md" className={classes.questionTitle}>
              What do users say about {listing.name}?
            </Typography>
          </div>
          <Typography
            variant="p"
            className={classes.description}
            dangerouslySetInnerHTML={{
              __html: reviewSummary.overview.replace(/\n/g, '<br>'),
            }}
          />

          <div className={classes.titleSection}>
            {reviewSummary.topics.length > 1 && (
              <Typography variant="heading-sm" className={classes.learnMoreTitle}>
                Select to learn more
              </Typography>
            )}
          </div>

          <div className={classes.chipsSection}>{renderChips}</div>

          <Divider className={classes.divider} />

          <div className={classes.chipContentSection}>{renderChipsContent}</div>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default ReviewsSummary
